import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AISection from "../AISection";
import Animation from "../Animation";
import Hero from "../Hero";
import IntroScreen from "../IntroScreen";
import NHISection from "../NHISection";
import Principles from "../Principles";
import ShiftCarousel from "../ShiftCarousel";
import HeroCarousel from "../HeroCarousel";

/*
 * This resets only when the browser page is refreshed.
 * It does not reset during React Router navigation.
 */
let homeVisitedInCurrentPage = false;

export default function Home() {
  const location = useLocation();

  const [stage, setStage] = useState(() => {
    const navigationEntry = performance.getEntriesByType("navigation")[0];

    const isRefresh = navigationEntry?.type === "reload";

    const isInternalHomeNavigation = location.state?.skipIntro === true;

    /*
     * Play only once in the current browser document.
     *
     * Play when:
     * - The website opens directly on Home.
     * - The browser is refreshed on Home.
     *
     * Skip when:
     * - Navigating to Home through React Router.
     * - Navigating to /#shift, /#ai, etc.
     * - Returning to Home after visiting another page.
     */
    const shouldPlayIntro =
      !homeVisitedInCurrentPage && (isRefresh || !isInternalHomeNavigation);

    // return shouldPlayIntro ? "video" : "website";
    return shouldPlayIntro ? "animation" : "website";
  });

  const websiteVisible = stage === "website";

  useEffect(() => {
    /*
     * The timeout prevents React StrictMode's development remount
     * from incorrectly skipping the intro.
     */
    const timeout = window.setTimeout(() => {
      homeVisitedInCurrentPage = true;
    }, 0);

    return () => {
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (!websiteVisible) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.14,
      },
    );

    const revealElements = document.querySelectorAll(".reveal");

    revealElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [websiteVisible]);

  useEffect(() => {
    const openingActive = stage !== "website";

    document.body.classList.toggle("intro-active", openingActive);

    return () => {
      document.body.classList.remove("intro-active");
    };
  }, [stage]);

  return (
    <>
      {stage === "video" && (
        <IntroScreen onComplete={() => setStage("animation")} />
      )}

      {stage === "animation" && (
        <Animation onComplete={() => setStage("website")} />
      )}

      {stage === "website" && (
        <div className="site-shell site-shell--visible">
          <HeroCarousel />
          <Hero />
          <ShiftCarousel />
          <AISection />
          <NHISection />
          <Principles />
        </div>
      )}
    </>
  );
}
